import { lazy as reactLazy } from 'react';

/**
 * Восстановление после ChunkLoadError.
 *
 * После деплоя хэши файлов в /static/js меняются, а образ nginx пересобирается
 * с нуля — старые чанки исчезают с сервера. Браузер (особенно Safari) может
 * держать в кэше прошлый index.html и запрашивать чанки, которых уже нет:
 * запрос отдаёт 404 или сам index.html вместо JS, и приложение падает.
 *
 * Лечение — один раз перезагрузить страницу, чтобы получить свежий index.html.
 * Всё остальное здесь — защита от бесконечного цикла перезагрузок.
 */

const STATE_KEY = 'bil:chunk-reload';
const URL_PARAM = '_cb';
const MAX_RELOADS = 2;
// Если прошлая перезагрузка была меньше этого времени назад, значит мы
// в цикле: свежий index.html не помог, и повторять уже бессмысленно.
const MIN_INTERVAL_MS = 10 * 1000;
// Столько приложение должно проработать без сбоев, чтобы счётчик обнулился
// (иначе вкладка, открытая несколько деплоев подряд, перестанет лечиться).
const HEALTHY_AFTER_MS = 60 * 1000;

// Перезагрузка уже запущена в этом сеансе — второй раз не дёргаем.
let reloadStarted = false;
let storageChecked = false;
let sessionStore = null;

const CHUNK_ERROR_PATTERNS = [
  /loading chunk \S+ failed/i,
  /loading css chunk \S+ failed/i,
  /chunkloaderror/i,
  /failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /importing a module script failed/i,
];

// Признаки того, что вместо JS отдали HTML (index.html на месте пропавшего
// чанка). Проверяем только для ошибок, пришедших из динамического import,
// иначе легко спутать с «Unexpected token <» при разборе ответа API.
const HTML_INSTEAD_OF_JS_PATTERNS = [
  /unexpected token '?</i,
  /expected expression, got '</i,
  /invalid or unexpected token/i,
];

/** Доступен ли sessionStorage (в приватном режиме Safari может бросать). */
function getSessionStore() {
  if (storageChecked) return sessionStore;
  storageChecked = true;
  try {
    const store = window.sessionStorage;
    store.setItem(STATE_KEY + ':probe', '1');
    store.removeItem(STATE_KEY + ':probe');
    sessionStore = store;
  } catch (e) {
    sessionStore = null;
  }
  return sessionStore;
}

function paramFromUrl(name) {
  const match = new RegExp('[?&]' + name + '=([^&#]*)').exec(
    window.location.search,
  );
  return match ? decodeURIComponent(match[1]) : null;
}

function searchWithoutMarker() {
  const search = window.location.search
    .replace(new RegExp('[?&]' + URL_PARAM + '=[^&#]*', 'g'), '')
    .replace(/^&/, '?');
  return search === '?' ? '' : search;
}

/**
 * Сколько перезагрузок уже было. Счётчик дублируется в URL: без него
 * приватный режим без sessionStorage ушёл бы в цикл.
 */
function readAttempts() {
  const store = getSessionStore();
  let count = 0;
  let at = 0;
  if (store) {
    try {
      const saved = JSON.parse(store.getItem(STATE_KEY) || 'null');
      if (saved) {
        count = Number(saved.count) || 0;
        at = Number(saved.at) || 0;
      }
    } catch (e) {
      // повреждённое значение — считаем, что перезагрузок не было
    }
  }
  const fromUrl = Number(paramFromUrl(URL_PARAM)) || 0;
  return { count: Math.max(count, fromUrl), at };
}

function buildReloadUrl(attempt) {
  const search = searchWithoutMarker();
  const separator = search ? '&' : '?';
  return (
    window.location.origin +
    window.location.pathname +
    search +
    separator +
    URL_PARAM +
    '=' +
    attempt +
    window.location.hash
  );
}

/** Является ли ошибка сбоем загрузки чанка. */
export function isChunkLoadError(error, { fromImport = false } = {}) {
  if (!error) return false;
  if (error.name === 'ChunkLoadError') return true;
  if (error.code === 'CSS_CHUNK_LOAD_FAILED') return true;
  const message = String(error.message || error);
  if (CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(message))) return true;
  return (
    fromImport &&
    HTML_INSTEAD_OF_JS_PATTERNS.some((pattern) => pattern.test(message))
  );
}

/**
 * Перезагрузить страницу, чтобы забрать свежий index.html.
 * Возвращает true, если перезагрузка запущена (значит показывать ошибку
 * пользователю не нужно), и false, если лимит попыток исчерпан.
 */
export function recoverFromChunkError(error) {
  if (reloadStarted) return true;

  const { count, at } = readAttempts();
  const now = new Date().getTime();
  if (count >= MAX_RELOADS) return false;
  if (at && now - at < MIN_INTERVAL_MS) return false;

  const attempt = count + 1;
  reloadStarted = true;

  const store = getSessionStore();
  if (store) {
    try {
      store.setItem(STATE_KEY, JSON.stringify({ count: attempt, at: now }));
    } catch (e) {
      // не смогли записать — счётчик останется в URL
    }
  }

  // eslint-disable-next-line no-console
  console.warn(
    '[chunk] не удалось загрузить чанк, перезагружаю страницу',
    error,
  );
  // replace, а не reload: параметр _cb гарантированно обходит кэш index.html
  // и не засоряет историю браузера.
  window.location.replace(buildReloadUrl(attempt));
  return true;
}

/** Убрать служебный параметр из адресной строки после успешной загрузки. */
function cleanReloadMarker() {
  if (!paramFromUrl(URL_PARAM)) return;
  // Без sessionStorage параметр в URL — единственный счётчик попыток,
  // убирать его нельзя.
  if (!getSessionStore()) return;
  if (!window.history || !window.history.replaceState) return;
  window.history.replaceState(
    window.history.state,
    '',
    window.location.pathname + searchWithoutMarker() + window.location.hash,
  );
}

function forgetAttempts() {
  const store = getSessionStore();
  if (!store) return;
  try {
    store.removeItem(STATE_KEY);
  } catch (e) {
    // ничего страшного
  }
}

/**
 * Глобальные перехватчики: динамический import ловится обёрткой lazy ниже,
 * а сюда попадают сбои загрузки <script>/<link> и необработанные промисы.
 */
export function installChunkRecovery() {
  cleanReloadMarker();

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event && event.reason;
    if (isChunkLoadError(reason) && recoverFromChunkError(reason)) {
      event.preventDefault();
    }
  });

  window.addEventListener(
    'error',
    (event) => {
      const target = event && event.target;
      // Сбой загрузки ресурса приходит на самом элементе и не всплывает,
      // поэтому слушаем на фазе перехвата.
      if (target && target !== window && target.tagName) {
        const url = target.src || target.href || '';
        if (/\/static\/(js|css)\//.test(url)) {
          recoverFromChunkError(new Error('Не загрузился ассет ' + url));
        }
        return;
      }
      if (isChunkLoadError(event && event.error)) {
        recoverFromChunkError(event.error);
      }
    },
    true,
  );

  // Приложение живо и работает — прошлые попытки больше не в счёт.
  window.setTimeout(forgetAttempts, HEALTHY_AFTER_MS);
}

/**
 * Замена React.lazy: повторяет импорт один раз (спасает от обрыва сети)
 * и перезагружает страницу, если чанка на сервере больше нет.
 */
export function lazy(factory) {
  return reactLazy(() =>
    factory().catch((error) => {
      if (!isChunkLoadError(error, { fromImport: true })) throw error;
      return new Promise((resolve) => window.setTimeout(resolve, 400))
        .then(factory)
        .catch((retryError) => {
          if (recoverFromChunkError(retryError)) {
            // Перезагрузка уже идёт — держим Suspense-фоллбэк вместо ошибки.
            return new Promise(() => {});
          }
          throw retryError;
        });
    }),
  );
}

export default lazy;
